import { customElement, property } from 'lit/decorators.js';
import { getProjectNumberFormatted } from '../../dealer-franchisee/projects/data/project-helper-functions';
import { html } from 'lit';
import { ProjectSummaryTable, ProjectSummaryTableOptions } from './project-list-view';
import { LangProjectPicker } from '../../../localization/scripts/projects/project-picker-lang';
import { ViewProjectSummary } from '../../api/dealer-api-interface-project';
import { WebModuleTable, WebModuleTableColumnDef } from '../../../webmodule-common/components/src/webmodule-components';
import { staleIcon } from '../../../webmodule-common/other/ui/icons/icon-notification-signal';

export interface ProjectPickerOptions extends ProjectSummaryTableOptions {
  onProjectSelected?: (projectSummary: ViewProjectSummary) => void;
  selectedProject?: ViewProjectSummary;
  clientId?: string;
}

@customElement('wm-projectsummarypickerbrowser')
export class ProjectPickerBrowser extends ProjectSummaryTable {
  onProjectSelected?: (projectSummary: ViewProjectSummary) => void;

  @property({ type: Object })
  selectedProject?: ViewProjectSummary;

  constructor(options: ProjectPickerOptions) {
    super(options);
    this.onProjectSelected = options.onProjectSelected;
    this.selectedProject = options.selectedProject;
    this.clickrows = true;
    this.selectMode = 'single';
  }

  override isDataLoadDelayed(): boolean {
    return false;
  }

  getColumns(): WebModuleTableColumnDef[] {
    const checkTemplate = (value: ViewProjectSummary) => {
      const checkClass = value.id === this.selectedProject?.id ? 'fa-solid fa-circle-dot' : 'fa-regular fa-circle';
      return html`<span class="ticket-owner-check">
        <i class="fa ${checkClass}"></i>
      </span>`;
    };

    // Column sizing moves from the Bootstrap `colpx-*` / `colpxmax-*` classes
    // (which don't pierce the new table's shadow DOM) to the column width model.
    return [
      {
        title: '', // no localization for empty title
        fieldName: 'id',
        sortable: false,
        classes: 'project-picker-id',
        width: '40px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as ViewProjectSummary;
          return checkTemplate(row);
        }
      },
      {
        title: () => LangProjectPicker.projectNumberTitle(),
        fieldName: 'number',
        sortable: true,
        classes: 'project-picker-number',
        width: '180px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as ViewProjectSummary;
          return this.stale(row)
            ? // eslint-disable-next-line @typescript-eslint/no-base-to-string
              html`${staleIcon()}${getProjectNumberFormatted(row)}`
            : getProjectNumberFormatted(row);
        }
      },
      {
        title: () => LangProjectPicker.projectTitleTitle(),
        fieldName: 'title',
        sortable: true,
        classes: 'project-picker-title',
        minWidth: '300px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as ViewProjectSummary;
          return row.title;
        }
      },
      {
        title: () => LangProjectPicker.clientTitle(),
        fieldName: 'clientId',
        sortable: false,
        classes: 'project-picker-client',
        width: '240px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const row = item as ViewProjectSummary;
          return this.getClientDisplayValue(row.clientId);
        }
      }
    ];
  }

  // eslint-disable-next-line @typescript-eslint/require-await
  public async rowClicked(e: CustomEvent<{ table: WebModuleTable; item: ViewProjectSummary }>): Promise<void> {
    e.stopImmediatePropagation();
    e.preventDefault();

    // eslint-disable-next-line @typescript-eslint/require-await
    this.eventHandler(async () => {
      this.onProjectSelected?.(e.detail.item);
      this.selectedProject = e.detail.item;
    });
  }
}
